import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, FileText, Users, ClipboardCheck, Mail, Settings as SettingsIcon,
  LogOut, Printer, Trash2, Bell, Eye, X, Save, Send, RefreshCw, Download, UserPlus, KeyRound, Copy, Check,
} from "lucide-react";
import api, { formatApiErrorDetail, API, TOKEN_KEY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { COMPANY } from "@/lib/content";

const FIELD_LABELS = {
  full_name: "Full Name", phone: "Phone", email: "Email", location: "Location", position: "Position",
  years_construction: "Years Construction", years_fiber: "Years Fiber", equipment_operated: "Equipment Operated",
  has_license: "Driver's License", has_cdl: "CDL", willing_travel: "Willing to Travel",
  available_overnight: "Available Overnight", desired_pay: "Desired Pay", start_date: "Start Date",
  experience_notes: "Experience", company_name: "Company", crew_types: "Crew Types",
  address: "Address", city_state_zip: "City/State/ZIP", ssn_last4: "SSN (last 4)", dob: "Date of Birth",
  emergency_name: "Emergency Contact", emergency_phone: "Emergency Phone", emergency_relation: "Relationship",
  filing_status: "Filing Status", dependents: "Dependents", citizenship_status: "Citizenship Status",
  w4_multiple_jobs: "W-4 Multiple Jobs", w4_children_amt: "W-4 Children Credit", w4_other_dependents_amt: "W-4 Other Dependents", w4_dependents_total: "W-4 Total Credits", w4_other_income: "W-4 Other Income", w4_deductions: "W-4 Deductions", w4_extra_withholding: "W-4 Extra Withholding", w4_exempt: "W-4 Exempt",
  i9_other_names: "I-9 Other Names Used", i9_citizenship: "I-9 Citizenship/Status", i9_uscis_number: "I-9 USCIS/A-Number", i9_i94_number: "I-9 Form I-94 Number", i9_passport_number: "I-9 Passport Number", i9_passport_country: "I-9 Passport Country", i9_work_auth_expiration: "I-9 Work Auth Expiration",
  bank_name: "Bank", routing_number: "Routing #", account_number: "Account #",
  handbook_ack: "Handbook Acknowledged", drug_free_ack: "Drug-Free Acknowledged", safety_ack: "Safety Acknowledged",
  name: "Name", company: "Company", subject: "Subject", message: "Message",
};

const HIDDEN = ["id", "_id", "type", "status", "photo_file", "signature_file", "resume_file", "license_file", "resume_name", "license_name"];

const STATUS_OPTIONS = {
  applications: ["new", "reviewed", "interviewing", "hired", "rejected"],
  subcontractors: ["new", "reviewed", "interviewing", "hired", "rejected"],
  onboarding: ["new", "reviewed", "processed", "archived"],
  contacts: ["new", "read", "archived"],
};

const STATUS_COLORS = {
  new: "bg-wcc-red/10 text-wcc-red border-wcc-red/30",
  reviewed: "bg-wcc-blue/10 text-wcc-blue border-wcc-blue/30",
  interviewing: "bg-amber-100 text-amber-700 border-amber-300",
  hired: "bg-green-100 text-green-700 border-green-300",
  processed: "bg-green-100 text-green-700 border-green-300",
  read: "bg-wcc-blue/10 text-wcc-blue border-wcc-blue/30",
  rejected: "bg-gray-100 text-gray-500 border-gray-300",
  archived: "bg-gray-100 text-gray-500 border-gray-300",
};

const statusCollection = (kind) => (kind === "subcontractors" ? "applications" : kind);

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "applications", label: "Applications", icon: FileText },
  { key: "subcontractors", label: "Subcontractors", icon: Users },
  { key: "onboarding", label: "Onboarding", icon: ClipboardCheck },
  { key: "contacts", label: "Messages", icon: Mail },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

export default function Portal() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [onboards, setOnboards] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ready && !user) nav("/portal/login");
  }, [ready, user, nav]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, o, c] = await Promise.all([
        api.get("/stats"), api.get("/applications"), api.get("/onboarding"), api.get("/contacts"),
      ]);
      setStats(s.data); setApps(a.data); setOnboards(o.data); setContacts(c.data);
    } catch (err) {
      toast.error("Failed to load data.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) loadAll(); }, [user, loadAll]);

  // Auto-refresh every 30s while the portal is open — powers the notification bell.
  useEffect(() => {
    if (!user) return;
    const t = setInterval(() => { loadAll(); }, 30000);
    return () => clearInterval(t);
  }, [user, loadAll]);

  // Merge everything into a single "unread" list (status === "new") sorted newest-first.
  const unread = useMemo(() => {
    const rows = [
      ...apps.map((a) => ({ ...a, _kind: a.type === "subcontractor" ? "subcontractors" : "applications" })),
      ...onboards.map((o) => ({ ...o, _kind: "onboarding" })),
      ...contacts.map((c) => ({ ...c, _kind: "contacts" })),
    ].filter((r) => (r.status || "new") === "new");
    rows.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return rows;
  }, [apps, onboards, contacts]);

  // When new items appear during a polling cycle, toast + soft beep so the owner
  // knows immediately without leaving the browser tab.
  const prevUnreadCount = useRef(null);
  useEffect(() => {
    if (prevUnreadCount.current === null) {
      prevUnreadCount.current = unread.length;
      return;
    }
    if (unread.length > prevUnreadCount.current) {
      const delta = unread.length - prevUnreadCount.current;
      toast.success(`${delta} new ${delta === 1 ? "submission" : "submissions"} arrived.`, {
        action: { label: "View", onClick: () => setNotifOpen(true) },
      });
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = "sine"; osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.0001, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
        osc.start(); osc.stop(ctx.currentTime + 0.4);
      } catch { /* audio blocked; toast is enough */ }
    }
    prevUnreadCount.current = unread.length;
  }, [unread.length]);

  const [notifOpen, setNotifOpen] = useState(false);

  const del = async (kind, id) => {
    const map = { applications: "applications", subcontractors: "applications", onboarding: "onboarding", contacts: "contacts" };
    try {
      await api.delete(`/${map[kind]}/${id}`);
      toast.success("Deleted.");
      setDetail(null);
      loadAll();
    } catch { toast.error("Delete failed."); }
  };

  const updateStatus = async (kind, id, status) => {
    const coll = statusCollection(kind);
    try {
      await api.patch(`/${coll}/${id}/status`, { status });
      const patch = (arr) => arr.map((r) => (r.id === id ? { ...r, status } : r));
      setApps(patch); setOnboards(patch); setContacts(patch);
      setDetail((d) => (d && d.id === id ? { ...d, status } : d));
      toast.success(`Marked as ${status}.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update status.");
    }
  };

  const exportCsv = async (kind) => {
    const coll = statusCollection(kind);
    try {
      const res = await api.get(`/export/${coll}.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `westcoral_${coll}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV exported.");
    } catch {
      toast.error("Export failed.");
    }
  };

  const enablePush = async () => {    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return toast.error("Push not supported on this browser.");
      const perm = await Notification.requestPermission();
      if (perm !== "granted") return toast.error("Notifications permission denied.");
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
      await api.post("/push/subscribe", sub.toJSON());
      toast.success("Push notifications enabled on this device.");
    } catch (err) {
      toast.error("Could not enable push notifications.");
    }
  };

  const testPush = async () => {
    try { await api.post("/push/test"); toast.success("Test notification sent."); }
    catch { toast.error("Failed to send test."); }
  };

  const testEmail = async () => {
    try { await api.post("/email/test"); toast.success("Test email sent to all admins."); }
    catch { toast.error("Failed to send test email."); }
  };

  const doLogout = () => { logout(); nav("/portal/login"); };

  if (!ready || !user) return <div className="min-h-screen grid place-items-center bg-wcc-paper mono text-sm text-wcc-gray">Loading…</div>;

  const apisOnly = apps.filter((a) => a.type !== "subcontractor");
  const subsOnly = apps.filter((a) => a.type === "subcontractor");

  return (
    <div className="min-h-screen bg-wcc-paper flex">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-wcc-navy text-white shrink-0 sticky top-0 h-screen" data-testid="portal-sidebar">
        <div className="p-6 border-b border-white/10 flex items-center gap-3">
          <img src="/assets/logo.png" alt="" className="h-10 w-auto bg-white rounded-sm p-1" />
          <div className="leading-none">
            <p className="font-display text-sm font-semibold uppercase">West Coral</p>
            <p className="mono text-[9px] tracking-widest text-white/40 uppercase">Owner Portal</p>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => (
            <button key={n.key} onClick={() => setTab(n.key)} data-testid={`portal-nav-${n.key}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors ${tab === n.key ? "bg-wcc-red text-white" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button onClick={doLogout} data-testid="portal-logout"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium text-white/60 hover:text-white hover:bg-white/5 transition-colors">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Topbar */}
        <div className="sticky top-0 z-20 bg-white border-b border-wcc-ink/10 px-5 sm:px-8 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-display text-xl font-semibold text-wcc-ink capitalize">{tab}</h1>
            <p className="mono text-[10px] uppercase tracking-widest text-wcc-gray">{user.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <NotifBell count={unread.length} open={notifOpen} onOpenChange={setNotifOpen} items={unread} onView={(r) => { setNotifOpen(false); setDetail(r); }} />
            <button onClick={loadAll} data-testid="portal-refresh" className="p-2.5 border border-wcc-ink/15 hover:border-wcc-red text-wcc-ink hover:text-wcc-red transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            {/* mobile nav select */}
            <select value={tab} onChange={(e) => setTab(e.target.value)} className="md:hidden border border-wcc-ink/15 py-2 px-3 text-sm" data-testid="portal-mobile-nav">
              {NAV.map((n) => <option key={n.key} value={n.key}>{n.label}</option>)}
            </select>
          </div>
        </div>

        <div className="p-5 sm:p-8">
          {tab === "overview" && <Overview stats={stats} onboards={onboards} apps={apps} contacts={contacts} setTab={setTab} onView={setDetail} />}
          {tab === "applications" && <Table rows={apisOnly} kind="applications" onView={setDetail} onStatus={updateStatus} onExport={exportCsv} emptyLabel="No job applications yet." />}
          {tab === "subcontractors" && <Table rows={subsOnly} kind="subcontractors" onView={setDetail} onStatus={updateStatus} onExport={exportCsv} emptyLabel="No subcontractor inquiries yet." nameKey="full_name" subKey="company_name" />}
          {tab === "onboarding" && <Table rows={onboards} kind="onboarding" onView={setDetail} onStatus={updateStatus} onExport={exportCsv} emptyLabel="No onboarding forms yet." />}
          {tab === "contacts" && <Table rows={contacts} kind="contacts" onView={setDetail} onStatus={updateStatus} onExport={exportCsv} emptyLabel="No messages yet." nameKey="name" subKey="subject" />}
          {tab === "settings" && <SettingsPanel onEnablePush={enablePush} onTestPush={testPush} onTestEmail={testEmail} />}
        </div>
      </main>

      <DetailDialog record={detail} onClose={() => setDetail(null)} onDelete={del} onStatus={updateStatus} />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, onClick }) {
  return (
    <button onClick={onClick} className="text-left bg-white border border-wcc-ink/10 p-6 hover:border-wcc-red transition-colors group" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-4">
        <Icon className="w-6 h-6 text-wcc-red" />
        <span className="mono text-[10px] uppercase tracking-widest text-wcc-gray group-hover:text-wcc-red transition-colors">View →</span>
      </div>
      <p className="font-display text-4xl font-semibold text-wcc-ink">{value ?? "—"}</p>
      <p className="mono text-[11px] uppercase tracking-widest text-wcc-gray mt-1">{label}</p>
    </button>
  );
}

const KIND_LABEL = { applications: "Application", subcontractors: "Subcontractor", onboarding: "Onboarding", contacts: "Message" };

function NotifBell({ count, open, onOpenChange, items, onView }) {
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && !btnRef.current.contains(e.target)) {
        onOpenChange(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onOpenChange]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => onOpenChange(!open)}
        data-testid="notif-bell"
        aria-label={`${count} new ${count === 1 ? "notification" : "notifications"}`}
        className="relative p-2.5 border border-wcc-ink/15 hover:border-wcc-red text-wcc-ink hover:text-wcc-red transition-colors"
      >
        <Bell className="w-4 h-4" />
        {count > 0 && (
          <span data-testid="notif-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-wcc-red text-white text-[10px] font-bold flex items-center justify-center">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      {open && (
        <div ref={panelRef} data-testid="notif-panel"
          className="absolute right-0 mt-2 w-[340px] max-h-[70vh] overflow-y-auto bg-white border border-wcc-ink/15 shadow-xl z-50">
          <div className="px-4 py-3 border-b border-wcc-ink/10 flex items-center justify-between sticky top-0 bg-white">
            <div>
              <p className="font-display text-sm font-semibold text-wcc-ink">Notifications</p>
              <p className="mono text-[10px] uppercase tracking-widest text-wcc-gray">{count} unread</p>
            </div>
            <button onClick={() => onOpenChange(false)} className="text-wcc-gray hover:text-wcc-ink" data-testid="notif-close"><X className="w-4 h-4" /></button>
          </div>
          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-wcc-ink/50" data-testid="notif-empty">You&apos;re all caught up.</p>
          ) : (
            <ul className="divide-y divide-wcc-ink/10">
              {items.slice(0, 25).map((r) => (
                <li key={`${r._kind}-${r.id}`}>
                  <button
                    onClick={() => onView(r)}
                    data-testid={`notif-item-${r.id}`}
                    className="w-full text-left px-4 py-3 hover:bg-wcc-paper transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="mono text-[9px] uppercase tracking-widest text-wcc-red">{KIND_LABEL[r._kind] || r._kind}</span>
                      <span className="mono text-[9px] uppercase tracking-widest text-wcc-gray">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</span>
                    </div>
                    <p className="text-sm text-wcc-ink font-medium truncate">{r.full_name || r.name || "—"}</p>
                    <p className="text-xs text-wcc-ink/60 truncate">{r.position || r.subject || r.company_name || r.message || ""}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Overview({ stats, onboards, apps, contacts, setTab, onView }) {
  const recent = [...apps.map((a) => ({ ...a, _k: a.type === "subcontractor" ? "subcontractors" : "applications" })),
    ...onboards.map((o) => ({ ...o, _k: "onboarding" })),
    ...contacts.map((c) => ({ ...c, _k: "contacts" }))]
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 8);

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Applications" value={stats?.applications} icon={FileText} onClick={() => setTab("applications")} />
        <StatCard label="Subcontractors" value={stats?.subcontractors} icon={Users} onClick={() => setTab("subcontractors")} />
        <StatCard label="Onboarding" value={stats?.onboarding} icon={ClipboardCheck} onClick={() => setTab("onboarding")} />
        <StatCard label="Messages" value={stats?.contacts} icon={Mail} onClick={() => setTab("contacts")} />
      </div>
      <div className="bg-white border border-wcc-ink/10">
        <div className="px-6 py-4 border-b border-wcc-ink/10"><h3 className="font-display text-lg font-medium text-wcc-ink">Recent Activity</h3></div>
        {recent.length === 0 ? (
          <p className="p-6 text-wcc-ink/50">No submissions yet.</p>
        ) : (
          <ul className="divide-y divide-wcc-ink/10">
            {recent.map((r) => (
              <li key={r.id} className="px-6 py-4 flex items-center justify-between hover:bg-wcc-paper transition-colors">
                <div className="min-w-0">
                  <p className="font-medium text-wcc-ink truncate flex items-center gap-2">{r.full_name || r.name} <span className="mono text-[10px] uppercase tracking-widest text-wcc-red">{r._k}</span> <StatusBadge status={r.status} /></p>
                  <p className="text-sm text-wcc-ink/50 truncate">{r.position || r.subject || r.company_name || "—"} · {fmtDate(r.created_at)}</p>
                </div>
                <button onClick={() => onView({ ...r, _kind: r._k })} className="text-wcc-navy hover:text-wcc-red transition-colors p-2" data-testid={`recent-view-${r.id}`}><Eye className="w-4 h-4" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const cls = STATUS_COLORS[status] || "bg-gray-100 text-gray-500 border-gray-300";
  return <span className={`mono text-[9px] uppercase tracking-widest border px-2 py-1 ${cls}`}>{status || "new"}</span>;
}

function Table({ rows, kind, onView, onStatus, onExport, emptyLabel, nameKey = "full_name", subKey = "position" }) {
  const options = STATUS_OPTIONS[kind] || ["new"];
  return (
    <div data-testid={`table-wrap-${kind}`}>
      <div className="flex items-center justify-between mb-4">
        <p className="mono text-[11px] uppercase tracking-widest text-wcc-gray">{rows.length} {rows.length === 1 ? "record" : "records"}</p>
        {onExport && (
          <button onClick={() => onExport(kind)} data-testid={`export-${kind}`}
            className="inline-flex items-center gap-2 border border-wcc-ink/20 text-wcc-ink font-semibold px-4 py-2 rounded-sm hover:border-wcc-red hover:text-wcc-red transition-colors text-sm">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        )}
      </div>
      {!rows.length ? (
        <div className="bg-white border border-wcc-ink/10 p-12 text-center text-wcc-ink/50" data-testid={`empty-${kind}`}>{emptyLabel}</div>
      ) : (
        <div className="bg-white border border-wcc-ink/10 overflow-x-auto" data-testid={`table-${kind}`}>
          <table className="w-full text-left min-w-[640px]">
            <thead className="bg-wcc-paper border-b border-wcc-ink/10">
              <tr>
                <th className="px-5 py-3 mono text-[10px] uppercase tracking-widest text-wcc-gray">Name</th>
                <th className="px-5 py-3 mono text-[10px] uppercase tracking-widest text-wcc-gray hidden sm:table-cell">Detail</th>
                <th className="px-5 py-3 mono text-[10px] uppercase tracking-widest text-wcc-gray">Status</th>
                <th className="px-5 py-3 mono text-[10px] uppercase tracking-widest text-wcc-gray hidden md:table-cell">Received</th>
                <th className="px-5 py-3 mono text-[10px] uppercase tracking-widest text-wcc-gray text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-wcc-ink/10">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-wcc-paper transition-colors" data-testid={`row-${kind}-${r.id}`}>
                  <td className="px-5 py-4 font-medium text-wcc-ink">{r[nameKey] || r.name || "—"}</td>
                  <td className="px-5 py-4 text-wcc-ink/60 hidden sm:table-cell">{r[subKey] || r.subject || r.company_name || "—"}</td>
                  <td className="px-5 py-4">
                    <select value={r.status || "new"} onChange={(e) => onStatus(kind, r.id, e.target.value)} data-testid={`status-${kind}-${r.id}`}
                      className={`mono text-[10px] uppercase tracking-widest border px-2 py-1.5 cursor-pointer outline-none ${STATUS_COLORS[r.status || "new"]}`}>
                      {options.map((o) => <option key={o} value={o} className="bg-white text-wcc-ink">{o}</option>)}
                    </select>
                  </td>
                  <td className="px-5 py-4 text-wcc-ink/50 text-sm hidden md:table-cell">{fmtDate(r.created_at)}</td>
                  <td className="px-5 py-4 text-right">
                    <button onClick={() => onView({ ...r, _kind: kind })} data-testid={`view-${kind}-${r.id}`}
                      className="inline-flex items-center gap-1.5 mono text-[11px] uppercase tracking-widest text-wcc-navy hover:text-wcc-red transition-colors">
                      <Eye className="w-4 h-4" /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function printRecord(record) {
  const title = record.full_name || record.name || "Submission";
  const kind = (record._kind || "record").toUpperCase();
  const rows = Object.entries(record)
    .filter(([k, v]) => !HIDDEN.includes(k) && !k.startsWith("_") && v !== "" && v !== null && v !== undefined)
    .map(([k, v]) => `<tr><td class="l">${FIELD_LABELS[k] || k}</td><td class="v">${v === true ? "Yes" : v === false ? "No" : String(v)}</td></tr>`)
    .join("");
  const images = [];
  if (record.photo_file) images.push(`<div><p class="cap">Photo / ID</p><img src="${record.photo_file}"/></div>`);
  if (record.signature_file) images.push(`<div><p class="cap">Signature</p><img src="${record.signature_file}" class="sig"/></div>`);
  if (record.resume_file) images.push(`<p class="cap">Résumé attached: ${record.resume_name || "file"}</p>`);
  if (record.license_file) images.push(`<p class="cap">License/Cert attached: ${record.license_name || "file"}</p>`);

  const w = window.open("", "_blank");
  w.document.write(`<!doctype html><html><head><title>${title} — West Coral Communications</title>
  <style>
    *{font-family:Arial,Helvetica,sans-serif;box-sizing:border-box}
    body{margin:40px;color:#0a1224}
    .hd{display:flex;align-items:center;gap:16px;border-bottom:4px solid #d9232f;padding-bottom:16px;margin-bottom:24px}
    .hd img{height:56px}
    h1{font-size:22px;margin:0}
    .tag{font-size:11px;letter-spacing:2px;color:#d9232f;text-transform:uppercase}
    .meta{font-size:12px;color:#666;margin-bottom:20px}
    table{width:100%;border-collapse:collapse;margin-bottom:24px}
    td{border:1px solid #ddd;padding:8px 12px;font-size:13px;vertical-align:top}
    td.l{background:#f4f6f8;font-weight:bold;width:38%;text-transform:uppercase;font-size:11px;letter-spacing:.5px}
    .imgs{display:flex;gap:24px;flex-wrap:wrap}
    .imgs img{max-width:220px;border:1px solid #ddd;padding:4px}
    .imgs img.sig{background:#fff}
    .cap{font-size:11px;letter-spacing:1px;text-transform:uppercase;color:#666;margin:0 0 6px}
    .ft{margin-top:32px;font-size:11px;color:#999;border-top:1px solid #eee;padding-top:12px}
  </style></head><body>
  <div class="hd"><img src="/assets/logo.png"/><div><div class="tag">${kind}</div><h1>${title}</h1></div></div>
  <p class="meta">West Coral Communications LLC · ${COMPANY.address} · Received: ${fmtDate(record.created_at)}</p>
  <table>${rows}</table>
  <div class="imgs">${images.join("")}</div>
  <p class="ft">Confidential — West Coral Communications LLC</p>
  </body></html>`);
  w.document.close();
  setTimeout(() => { w.focus(); w.print(); }, 500);
}

function DetailDialog({ record, onClose, onDelete, onStatus }) {
  if (!record) return null;
  const entries = Object.entries(record).filter(([k, v]) => !HIDDEN.includes(k) && !k.startsWith("_") && v !== "" && v !== null && v !== undefined);
  const options = STATUS_OPTIONS[record._kind] || ["new"];
  return (
    <Dialog open={!!record} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto rounded-none border-t-4 border-wcc-red" data-testid="detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-wcc-ink flex items-center justify-between pr-6">
            <span>{record.full_name || record.name}</span>
            <span className="mono text-[10px] uppercase tracking-widest text-wcc-red">{record._kind}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-between gap-4 -mt-2">
          <p className="mono text-[10px] uppercase tracking-widest text-wcc-gray">Received {fmtDate(record.created_at)}</p>
          <div className="flex items-center gap-2">
            <span className="mono text-[9px] uppercase tracking-widest text-wcc-gray">Status</span>
            <select value={record.status || "new"} onChange={(e) => onStatus(record._kind, record.id, e.target.value)} data-testid="detail-status"
              className={`mono text-[10px] uppercase tracking-widest border px-2 py-1.5 cursor-pointer outline-none ${STATUS_COLORS[record.status || "new"]}`}>
              {options.map((o) => <option key={o} value={o} className="bg-white text-wcc-ink">{o}</option>)}
            </select>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mt-4">
          {entries.map(([k, v]) => (
            <div key={k} className="border-b border-wcc-ink/10 pb-2">
              <p className="mono text-[9px] uppercase tracking-widest text-wcc-gray">{FIELD_LABELS[k] || k}</p>
              <p className="text-wcc-ink text-sm break-words">{v === true ? "Yes" : v === false ? "No" : String(v)}</p>
            </div>
          ))}
        </div>

        {(record.photo_file || record.signature_file) && (
          <div className="flex gap-6 mt-4 flex-wrap">
            {record.photo_file && <div><p className="mono text-[9px] uppercase tracking-widest text-wcc-gray mb-2">Photo / ID</p><img src={record.photo_file} alt="photo" className="w-32 h-32 object-cover border border-wcc-ink/15" /></div>}
            {record.signature_file && <div><p className="mono text-[9px] uppercase tracking-widest text-wcc-gray mb-2">Signature</p><img src={record.signature_file} alt="signature" className="h-24 border border-wcc-ink/15 bg-white" /></div>}
          </div>
        )}
        {(record.resume_file || record.license_file) && (
          <div className="mt-4 space-y-2">
            {record.resume_file && <a href={record.resume_file} download={record.resume_name || "resume"} className="inline-block mono text-xs text-wcc-navy underline hover:text-wcc-red" data-testid="download-resume">↓ Download Résumé ({record.resume_name || "file"})</a>}
            {record.license_file && <a href={record.license_file} download={record.license_name || "license"} className="block mono text-xs text-wcc-navy underline hover:text-wcc-red" data-testid="download-license">↓ Download License/Cert ({record.license_name || "file"})</a>}
          </div>
        )}

        <div className="flex items-center gap-3 mt-8 pt-4 border-t border-wcc-ink/10">
          <button onClick={() => printRecord(record)} data-testid="detail-print"
            className="inline-flex items-center gap-2 bg-wcc-navy text-white font-semibold px-5 py-3 rounded-sm hover:bg-wcc-red transition-colors">
            <Printer className="w-4 h-4" /> Print / Save PDF
          </button>
          <button onClick={() => onDelete(record._kind, record.id)} data-testid="detail-delete"
            className="inline-flex items-center gap-2 border border-wcc-red text-wcc-red font-semibold px-5 py-3 rounded-sm hover:bg-wcc-red hover:text-white transition-colors">
            <Trash2 className="w-4 h-4" /> Delete
          </button>
          <button onClick={onClose} className="ml-auto p-3 text-wcc-gray hover:text-wcc-ink transition-colors" data-testid="detail-close"><X className="w-5 h-5" /></button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SettingsPanel({ onEnablePush, onTestPush, onTestEmail }) {
  const [gatePw, setGatePw] = useState("");
  const [isSet, setIsSet] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [revealed, setRevealed] = useState("");   // last-set plaintext, shown once
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/settings/gate-password").then(({ data }) => {
      setIsSet(!!data.is_set);
      setUpdatedAt(data.updated_at || null);
    }).catch(() => {});
  }, []);

  const save = async () => {
    if (gatePw.length < 4) return toast.error("Password must be at least 4 characters.");
    setSaving(true);
    try {
      const { data } = await api.put("/settings/gate-password", { password: gatePw });
      setIsSet(true);
      setUpdatedAt(data.updated_at);
      setRevealed(data.password);       // one-time reveal from server response
      setGatePw("");
      toast.success("Employee onboarding password updated. Copy it now — it won't be shown again.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setSaving(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(revealed);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed. Select and copy manually."); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <ChangeMyPasswordCard />

      <div className="bg-white border border-wcc-ink/10 p-8">
        <h3 className="font-display text-xl font-medium text-wcc-ink mb-1">Employee Onboarding Password</h3>
        <p className="text-sm text-wcc-ink/60 mb-6">The shared password new employees enter on the onboarding page. It is hashed at rest — the current value is not stored in plaintext. Set a new password below to rotate it; you&apos;ll see the new value once, so copy it before leaving this page.</p>

        <div className="mb-6 flex flex-wrap items-center gap-3">
          <span className={`mono text-[10px] uppercase tracking-widest border px-3 py-1.5 ${isSet ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"}`} data-testid="gate-status">
            {isSet ? "Password Configured" : "Not Configured"}
          </span>
          {updatedAt && <span className="mono text-[10px] uppercase tracking-widest text-wcc-gray">Last rotated {new Date(updatedAt).toLocaleString()}</span>}
        </div>

        {revealed && (
          <div className="mb-6 border-l-4 border-wcc-red bg-wcc-red/5 p-4" data-testid="gate-revealed">
            <p className="mono text-[10px] uppercase tracking-widest text-wcc-red mb-2">New password — shown only once</p>
            <div className="flex items-center gap-3">
              <code className="font-mono text-base text-wcc-ink bg-white border border-wcc-ink/15 px-3 py-2 flex-1 break-all">{revealed}</code>
              <button onClick={copy} data-testid="gate-copy" className="inline-flex items-center gap-1.5 border border-wcc-ink/20 px-3 py-2 hover:border-wcc-red hover:text-wcc-red transition-colors text-sm">
                {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
              </button>
            </div>
          </div>
        )}

        <label className="mono text-[10px] uppercase tracking-widest text-wcc-gray">New Password</label>
        <input value={gatePw} onChange={(e) => setGatePw(e.target.value)} placeholder="Enter a new onboarding password" data-testid="settings-gate-password"
          className="w-full bg-transparent border-b-2 border-wcc-ink/20 focus:border-wcc-red outline-none py-2.5 text-wcc-ink transition-colors" />
        <button onClick={save} disabled={saving || !gatePw} data-testid="settings-gate-save"
          className="mt-6 inline-flex items-center gap-2 bg-wcc-red text-white font-semibold px-6 py-3 rounded-sm hover:bg-wcc-red-deep disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Rotate Password"}
        </button>
      </div>

      <AdminsPanel />

      <div className="bg-white border border-wcc-ink/10 p-8">
        <h3 className="font-display text-xl font-medium text-wcc-ink mb-1">Push Notifications</h3>
        <p className="text-sm text-wcc-ink/60 mb-6">Get a push notification on this device whenever a new application or onboarding form is submitted. Install the app to your home screen for the best experience. Each admin can enable push on their own devices below.</p>
        <div className="flex flex-wrap gap-3">
          <button onClick={onEnablePush} data-testid="settings-enable-push"
            className="inline-flex items-center gap-2 bg-wcc-navy text-white font-semibold px-6 py-3 rounded-sm hover:bg-wcc-red transition-colors">
            <Bell className="w-4 h-4" /> Enable on This Device
          </button>
          <button onClick={onTestPush} data-testid="settings-test-push"
            className="inline-flex items-center gap-2 border border-wcc-ink/20 text-wcc-ink font-semibold px-6 py-3 rounded-sm hover:border-wcc-red hover:text-wcc-red transition-colors">
            <Send className="w-4 h-4" /> Send Test
          </button>
        </div>
      </div>

      <div className="bg-white border border-wcc-ink/10 p-8">
        <h3 className="font-display text-xl font-medium text-wcc-ink mb-1">Email Notifications</h3>
        <p className="text-sm text-wcc-ink/60 mb-6">Every admin listed above automatically receives a formatted email whenever a new job application, subcontractor inquiry, employee onboarding form, or contact message is submitted. Emails come from West Coral Communications and are delivered through Emergent&#39;s managed mail service — nothing for you to configure.</p>
        <button onClick={onTestEmail} data-testid="settings-test-email"
          className="inline-flex items-center gap-2 border border-wcc-ink/20 text-wcc-ink font-semibold px-6 py-3 rounded-sm hover:border-wcc-red hover:text-wcc-red transition-colors">
          <Mail className="w-4 h-4" /> Send Test Email to All Admins
        </button>
      </div>
    </div>
  );
}

function ChangeMyPasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters.");
    if (next !== confirm) return toast.error("New passwords do not match.");
    if (next === current) return toast.error("New password must be different from the current one.");
    setSaving(true);
    try {
      await api.post("/me/change-password", { current_password: current, new_password: next });
      toast.success("Password updated. Use it the next time you sign in.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update password.");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-wcc-ink/10 p-8" data-testid="my-password-card">
      <h3 className="font-display text-xl font-medium text-wcc-ink mb-1">Your Password</h3>
      <p className="text-sm text-wcc-ink/60 mb-6">Rotate your Owner Portal sign-in password. Only affects your account — other admins are unaffected.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="mono text-[10px] uppercase tracking-widest text-wcc-gray">Current Password</label>
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required autoComplete="current-password" data-testid="my-pw-current"
            className="w-full bg-transparent border-b-2 border-wcc-ink/20 focus:border-wcc-red outline-none py-2.5 text-wcc-ink transition-colors" />
        </div>
        <div>
          <label className="mono text-[10px] uppercase tracking-widest text-wcc-gray">New Password (min 8)</label>
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={8} autoComplete="new-password" data-testid="my-pw-new"
            className="w-full bg-transparent border-b-2 border-wcc-ink/20 focus:border-wcc-red outline-none py-2.5 text-wcc-ink transition-colors" />
        </div>
        <div>
          <label className="mono text-[10px] uppercase tracking-widest text-wcc-gray">Confirm New Password</label>
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} autoComplete="new-password" data-testid="my-pw-confirm"
            className="w-full bg-transparent border-b-2 border-wcc-ink/20 focus:border-wcc-red outline-none py-2.5 text-wcc-ink transition-colors" />
        </div>
      </div>
      <button type="submit" disabled={saving} data-testid="my-pw-submit"
        className="mt-6 inline-flex items-center gap-2 bg-wcc-navy text-white font-semibold px-6 py-3 rounded-sm hover:bg-wcc-red disabled:opacity-60 transition-colors">
        <KeyRound className="w-4 h-4" /> {saving ? "Updating…" : "Update My Password"}
      </button>
    </form>
  );
}

function AdminsPanel() {
  const [admins, setAdmins] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admins");
      setAdmins(data);
    } catch { toast.error("Failed to load admins."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Enter a valid email.");
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    setAdding(true);
    try {
      await api.post("/admins", { email: email.trim(), password, name: name.trim() });
      toast.success(`Admin ${email} added. They can now log in and enable push.`);
      setEmail(""); setName(""); setPassword("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add admin.");
    } finally { setAdding(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove admin ${a.email}? They will no longer be able to log in or receive push notifications.`)) return;
    try {
      await api.delete(`/admins/${a.id}`);
      toast.success(`Removed ${a.email}.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to remove admin.");
    }
  };

  return (
    <div className="bg-white border border-wcc-ink/10 p-8" data-testid="admins-panel">
      <h3 className="font-display text-xl font-medium text-wcc-ink mb-1">Admin / Owner Accounts</h3>
      <p className="text-sm text-wcc-ink/60 mb-6">Add trusted people to the portal so they can view submissions and receive push notifications on their own devices.</p>

      <ul className="divide-y divide-wcc-ink/10 mb-6 border-y border-wcc-ink/10" data-testid="admins-list">
        {loading && admins.length === 0 && (
          <li className="py-4 mono text-[11px] uppercase tracking-widest text-wcc-gray">Loading…</li>
        )}
        {admins.map((a) => (
          <li key={a.id} className="py-3 flex items-center justify-between gap-4" data-testid={`admin-row-${a.id}`}>
            <div className="min-w-0">
              <p className="text-wcc-ink font-medium truncate">{a.email} {a.is_self && <span className="mono text-[9px] text-wcc-gray uppercase tracking-widest">· you</span>}</p>
              <p className="mono text-[10px] uppercase tracking-widest text-wcc-gray">{a.name || "Admin"} · {a.is_primary ? "Primary Owner" : "Admin"}</p>
            </div>
            <button
              onClick={() => remove(a)}
              disabled={a.is_primary || a.is_self}
              data-testid={`admin-remove-${a.id}`}
              title={a.is_primary ? "Primary owner cannot be removed." : a.is_self ? "Sign in as another admin to remove yourself." : "Remove admin"}
              className="p-2 text-wcc-gray hover:text-wcc-red disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>

      <form onSubmit={add} className="grid sm:grid-cols-2 gap-4" data-testid="admin-add-form">
        <div>
          <label className="mono text-[10px] uppercase tracking-widest text-wcc-gray">Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-email"
            className="w-full bg-transparent border-b-2 border-wcc-ink/20 focus:border-wcc-red outline-none py-2.5 text-wcc-ink transition-colors" />
        </div>
        <div>
          <label className="mono text-[10px] uppercase tracking-widest text-wcc-gray">Name (optional)</label>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-name"
            className="w-full bg-transparent border-b-2 border-wcc-ink/20 focus:border-wcc-red outline-none py-2.5 text-wcc-ink transition-colors" />
        </div>
        <div className="sm:col-span-2">
          <label className="mono text-[10px] uppercase tracking-widest text-wcc-gray">Temporary Password (min 8 chars)</label>
          <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} data-testid="admin-password"
            className="w-full bg-transparent border-b-2 border-wcc-ink/20 focus:border-wcc-red outline-none py-2.5 text-wcc-ink transition-colors" />
          <p className="mono text-[10px] text-wcc-gray mt-1">Share this with the new admin securely. They can change it later.</p>
        </div>
        <div className="sm:col-span-2">
          <button type="submit" disabled={adding} data-testid="admin-add-submit"
            className="inline-flex items-center gap-2 bg-wcc-navy text-white font-semibold px-6 py-3 rounded-sm hover:bg-wcc-red disabled:opacity-60 transition-colors">
            <UserPlus className="w-4 h-4" /> {adding ? "Adding…" : "Add Admin"}
          </button>
        </div>
      </form>
    </div>
  );
}
